import { SelectHTMLAttributes, useState, useRef, useEffect, ReactNode, Children, isValidElement } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import IconNB from './IconNB';

interface SelectNBProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label?: string;
  error?: string;
  placeholder?: string;
  selectSize?: 'sm' | 'md';
  onChange?: (event: { target: { value: string } }) => void;
}

interface Option {
  value: string;
  label: string;
}

function parseOptions(children: ReactNode): Option[] {
  const options: Option[] = [];

  Children.forEach(children, child => {
    if (isValidElement(child) && child.type === 'option') {
      const { value, children: label } = child.props as { value: string; children: ReactNode };
      options.push({ value: String(value), label: String(label) });
    }
  });

  return options;
}

export default function SelectNB({
  label,
  error,
  className = '',
  id,
  children,
  value,
  placeholder = 'Select...',
  selectSize = 'md',
  onChange,
  ...props
}: SelectNBProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');
  const options = parseOptions(children);
  const selectedOption = options.find(opt => opt.value === value);

  // Close on outside click
  useClickOutside(containerRef, () => {
    setIsOpen(false);
    setFocusedIndex(-1);
  }, isOpen);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setFocusedIndex(-1);
        triggerRef.current?.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleTriggerClick = () => {
    setIsOpen(prev => !prev);
    if (!isOpen) {
      // Reset focused index when opening
      const selectedIdx = options.findIndex(opt => opt.value === value);
      setFocusedIndex(selectedIdx >= 0 ? selectedIdx : 0);
    }
  };

  const handleOptionClick = (optValue: string) => {
    if (onChange) {
      onChange({ target: { value: optValue } });
    }
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setFocusedIndex(options.length > 0 ? 0 : -1);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => (prev + 1) % options.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => (prev - 1 + options.length) % options.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < options.length) {
          handleOptionClick(options[focusedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        triggerRef.current?.focus();
        break;
    }
  };

  return (
    <div className="flex flex-col gap-1 relative" ref={containerRef}>
      {label && (
        <label
          htmlFor={selectId}
          className="font-nb font-bold text-sm text-nb-black"
        >
          {label}
        </label>
      )}
      <button
        ref={triggerRef}
        type="button"
        id={selectId}
        onClick={handleTriggerClick}
        onKeyDown={handleKeyDown}
        className={`
          bg-nb-white
          border-3 border-nb-black
          px-4 ${selectSize === 'sm' ? 'py-1.5 text-sm' : 'py-3 text-base'}
          font-nb
          text-left
          w-full
          flex
          justify-between
          items-center
          focus:border-nb-yellow
          focus:outline-none
          transition-transform
          ${error ? 'border-nb-red' : ''}
          ${className}
        `.replace(/\s+/g, ' ').trim()}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={selectedOption ? 'text-nb-black' : 'text-nb-text-muted'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <IconNB
          name="chevronRight"
          size={16}
          className={`transition-transform ${isOpen ? 'rotate-90' : 'rotate-0'}`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 min-w-full mt-1 bg-nb-white border-3 border-nb-black shadow-[4px_4px_0px_#000] z-50 max-h-60 overflow-y-auto whitespace-nowrap"
          role="listbox"
        >
          {options.map((opt, idx) => (
            <div
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              onClick={() => handleOptionClick(opt.value)}
              className={`
                px-4 py-2.5
                cursor-pointer
                font-nb
                text-sm
                border-b border-nb-black/10
                last:border-b-0
                ${opt.value === value ? 'bg-nb-yellow/30 font-bold' : 'hover:bg-nb-yellow'}
                ${idx === focusedIndex ? 'bg-nb-yellow' : ''}
              `.replace(/\s+/g, ' ').trim()}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
      {error && (
        <span className="text-xs text-nb-red">{error}</span>
      )}
    </div>
  );
}